import React from "react";
import styled from "styled-components";
import { format, parseISO } from "date-fns";
import { FiFileText, FiDownload } from "react-icons/fi";
import { MdKeyboardArrowDown } from "react-icons/md";


const FIREBASE_STORAGE = /firebasestorage\.googleapis\.com/i;

const isFirebaseFile = (url) => {
  if (!url) return false;
  return FIREBASE_STORAGE.test(url);
};

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|ico)(\?|$)/i;
const PDF_EXT = /\.pdf(\?|$)/i;
const isImageUrl = (url, name) => {
  if (!url) return false;
  if (name && IMAGE_EXT.test(name)) return true;
  return IMAGE_EXT.test(url) || /\.(cdn\.discordapp\.com|media\.discordapp\.net)/i.test(url);
};

const isPdfUrl = (url, name) => {
  if (!url) return false;
  if (name && PDF_EXT.test(name)) return true;
  return PDF_EXT.test(url);
};

// Giphy page URLs (e.g. https://giphy.com/gifs/lol-laugh-laughing-gj0QdZ9FgqGhOBNlFS) point to HTML; we need the direct GIF URL.
const GIPHY_PAGE = /giphy\.com\/gif(?:s)?\/(?:[^/]*\-)?([a-zA-Z0-9]{10,})(?:\?|$)/i;
const GIPHY_URL_IN_TEXT = /(https?:\/\/(?:www\.)?giphy\.com\/gif(?:s)?\/[^\s]+)/gi;
const isGiphyPageUrl = (url) => typeof url === "string" && GIPHY_PAGE.test(url);
const getGiphyDirectUrl = (pageUrl) => {
  const m = String(pageUrl).match(GIPHY_PAGE);
  if (!m || !m[1]) return null;
  return `https://media.giphy.com/media/${m[1]}/giphy.gif`;
};
/** Renders message content, embedding Giphy links as GIFs. */
function renderContentWithGiphy(content) {
  if (!content || typeof content !== "string") return null;
  const parts = content.split(GIPHY_URL_IN_TEXT);
  if (parts.length === 1) return <ContentText>{content}</ContentText>;
  return (
    <ContentText>
      {parts.map((part, i) => {
        if (isGiphyPageUrl(part)) {
          const direct = getGiphyDirectUrl(part);
          return direct ? (
            <InlineGif key={i} src={direct} alt="GIF" title={part} />
          ) : (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </ContentText>
  );
}

export function getMessageId(msg, index) {
  return msg.id ?? `i-${index}`;
}


const DEFAULT_AVATAR_COLOR = "#9333ea";

function getDisplayNameForAuthor(msgAuthor, groupMembers) {
  const discordUsername = (msgAuthor?.username ?? "").trim().toLowerCase();
  if (!discordUsername) return msgAuthor?.discord ?? msgAuthor?.username ?? "Unknown";
  const member = groupMembers?.find(
    (m) => (m.discord_username || "").trim().toLowerCase() === discordUsername
  );
  return member?.name ?? msgAuthor?.username ?? msgAuthor?.discord ?? "Unknown";
}

function getInitialsFromName(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getAvatarForAuthor(msgAuthor, groupMembers) {
  const discordUsername = (msgAuthor?.username ?? msgAuthor?.discord ?? "").trim();
  const member = groupMembers?.find(
    (m) => (m.discord_username || "").trim().toLowerCase() === discordUsername.toLowerCase()
  );
  const color = member?.color ? member.color : DEFAULT_AVATAR_COLOR;
  const initial = member
    ? (member.initial || getInitialsFromName(member.name) || "?")
    : (discordUsername.length >= 2
      ? discordUsername.slice(0, 2).toUpperCase()
      : (discordUsername.slice(0, 1) || "?").toUpperCase());
  return { color, initial };
}

function DiscordDraftSection({ discordDraft, maxHeight = 220, selectable = false, selectedMessageIds = null, onToggleMessage = null, onSelectAll = null, onDeselectAll = null, groupMembers = null }) {
  const messages = Array.isArray(discordDraft?.messages) ? discordDraft.messages : [];
  const messageCount = messages.length;
  const selectedSet = selectedMessageIds instanceof Set ? selectedMessageIds : new Set(selectedMessageIds ?? []);
  const selectedCount = messages.filter((m, i) => selectedSet.has(getMessageId(m, i))).length;
  const [expandedKeys, setExpandedKeys] = React.useState(new Set());
  const [loadingMap, setLoadingMap] = React.useState({});
  const setLoading = (key, value) => {
    setLoadingMap(prev => ({ ...prev, [key]: value }));
  };

  if (messageCount === 0) {
    return null;
  }

  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
  
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        setLoading(key, true);
      }
  
      return next;
    });
  };

  return (
    <Wrap>
      <Header>
        <MetaRow>
          <CountRow>
            <CountText>
              {messageCount} message{messageCount !== 1 ? "s" : ""}
            </CountText>
            {selectable && selectedMessageIds != null && (
              <SelectedPill>({selectedCount} selected)</SelectedPill>
            )}
          </CountRow>

          {selectable && onSelectAll && onDeselectAll && (
            <ActionsRow>
              <LinkBtn type="button" onClick={onSelectAll}>
                Select all
              </LinkBtn>
              <LinkBtnMuted type="button" onClick={onDeselectAll}>
                Deselect all
              </LinkBtnMuted>
            </ActionsRow>
          )}
        </MetaRow>
      </Header>
      <Card>
        <ScrollArea $maxHeight={maxHeight}>
          {messages.map((msg, index) => {
            const msgId = getMessageId(msg, index);
            const isSelected = selectable ? selectedSet.has(msgId) : true;

            const author = getDisplayNameForAuthor(msg.author, groupMembers);
            const avatar = getAvatarForAuthor(msg.author, groupMembers);
            const time = msg.timestamp
              ? format(
                typeof msg.timestamp === "string" ? parseISO(msg.timestamp) : new Date(msg.timestamp),
                "yyyy-MM-dd HH:mm"
              )
              : "";

            const content = (msg.content || "").trim();
            const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];

            return (
              <Row key={msgId} $dimmed={selectable && !isSelected}>
                {selectable && onToggleMessage && (
                  <CheckboxWrap>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleMessage(msgId)}
                      aria-label={`Include message from ${author}`}
                    />
                  </CheckboxWrap>
                )}

                <AvatarCircle $bg={avatar.color} data-tooltip={author}>
                  {avatar.initial}
                </AvatarCircle>
                <Body>
                  <TopLine>
                    <AuthorName>{author}</AuthorName>
                    {time && <TimeText>{time}</TimeText>}
                  </TopLine>

                  {content ? renderContentWithGiphy(content) : null}

                  {attachments.length > 0 && (
                    <Attachments>
                      {attachments.map((att, attIndex) => {
                        const url = att.url ?? att.proxyURL ?? att.proxyUrl;
                        const name = att.name ?? "attachment";
                        if (!url) return null;

                        const isFirebase = isFirebaseFile(url);
                        const giphyDirect = isGiphyPageUrl(url) ? getGiphyDirectUrl(url) : null;
                        const imageUrl = giphyDirect ?? (isImageUrl(url, name) ? url : null);
                        const isPdf = !imageUrl && isPdfUrl(url, name);

                        /* FIREBASE STORAGE FILES - Expandable preview UI */

                        if (isFirebase) {
                          const attachmentKey = `${msgId}:${att.id ?? att.url ?? att.proxyURL ?? att.proxyUrl ?? attIndex}`;
                          const isImg = IMAGE_EXT.test(name) || IMAGE_EXT.test(url);
                          const isTxt = /\.txt(\?|$)/i.test(name);

                          return (
                            <FileItemWrapper key={attachmentKey}>
                              <FileItem>
                                <FileDetails
                                  as="a"
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FileIcon aria-hidden>
                                    <FiFileText size={18} />
                                  </FileIcon>
                                  <FileName>{name}</FileName>
                                </FileDetails>

                                <ButtonRow>
                                  {(isImg || isPdf || isTxt) ? (
                                    <ExpandButton onClick={() => toggleExpand(attachmentKey)}>
                                      <ChevronIcon $open={expandedKeys.has(attachmentKey)} />
                                    </ExpandButton>
                                  ) : (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={name}
                                    >
                                      <FileDownload aria-hidden>
                                        <FiDownload size={16} />
                                      </FileDownload>
                                    </a>
                                  )}
                                </ButtonRow>
                              </FileItem>

                              {expandedKeys.has(attachmentKey) && (
                                <PreviewWindow>

                                  {loadingMap[attachmentKey] && (
                                    <PreviewLoading>Loading preview...</PreviewLoading>
                                  )}


                                  {isImg &&
                                    <PreviewImage
                                      src={url}
                                      alt={name}
                                      onLoad={() => setLoading(attachmentKey, false)}
                                      onError={() => setLoading(attachmentKey, false)}
                                      style={{ display: loadingMap[attachmentKey] ? "none" : "block" }}
                                    />
                                  }

                                  {isPdf && (
                                    <PreviewPdf
                                      src={url}
                                      title={name}
                                      onLoad={() => setLoading(attachmentKey, false)}
                                      style={{ display: loadingMap[attachmentKey] ? "none" : "block" }}
                                    />
                                  )}

                                  {isTxt && (
                                    <PreviewText>
                                      Loading preview...
                                    </PreviewText>
                                  )}
                                </PreviewWindow>
                              )}
                            </FileItemWrapper>
                          );
                        }


                        if (imageUrl) {
                          return (
                            <ImageLink
                              key={att.id ?? url}
                              href={imageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open image in new tab"
                            >
                              <ImageCard>
                                <ImagePreview src={imageUrl} alt={name} />
                                <ImageOverlay>
                                  <OverlayName title={name}>{name}</OverlayName>
                                </ImageOverlay>
                              </ImageCard>
                            </ImageLink>
                          );
                        }

                        return (
                          <FileCard
                            key={att.id ?? url}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={name}
                          >
                            <FileIcon aria-hidden>
                              <FiFileText size={18} />
                            </FileIcon>
                            <FileName>{name}</FileName>
                            <FileDownload aria-hidden>
                              <FiDownload size={16} />
                            </FileDownload>
                          </FileCard>
                        );
                      })}
                    </Attachments>
                  )}
                </Body>
              </Row>
            );
          })}
        </ScrollArea>
      </Card>

    </Wrap>
  );
}

/* Match AddActivityWindow section styling (SectionTitle + ContentWrapper) */
const Wrap = styled.div`
  margin-top: 8px;
  margin-bottom: 0.5rem;
  text-align: left;
`;


const Header = styled.div`
  margin-bottom: 14px;
`;


const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const CountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CountText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #334155;
`;

const SelectedPill = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #5865f2;
  background: rgba(88, 101, 242, 0.12);
  padding: 3px 10px;
  border-radius: 999px;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const LinkBtn = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: #5865f2;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const LinkBtnMuted = styled(LinkBtn)`
  color: #64748b;
  &:hover {
    color: #334155;
    text-decoration: none;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  text-align: left;
`;

const ScrollArea = styled.div`
  max-height: ${(p) => (p.$maxHeight ?? 280)}px;
  overflow-y: auto;
  text-align: left;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
  transition: background 120ms ease, opacity 120ms ease;

  opacity: ${(p) => (p.$dimmed ? 0.55 : 1)};

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CheckboxWrap = styled.div`
  padding-top: 2px;
  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #5865f2;
  }
`;

const AvatarCircle = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => p.$bg ?? DEFAULT_AVATAR_COLOR};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  position: relative;

  &[data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: #111827;
    color: #fff;
    font: 600 12px/1.2 Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    padding: 6px 8px;
    border-radius: 6px;
    white-space: nowrap;
    box-shadow: 0 6px 18px rgba(0,0,0,.15);
    pointer-events: none;
    z-index: 10;
  }

  &[data-tooltip]:hover::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #111827;
    pointer-events: none;
    z-index: 10;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const TopLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
`;

const AuthorName = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TimeText = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const ContentText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
`;

const InlineGif = styled.img`
  display: block;
  max-width: 100%;
  max-height: 200px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  margin-top: 6px;
  border: 1px solid #e5e7eb;
`;

const Attachments = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
`;

/* Image attachment card */
const ImageLink = styled.a`
  display: inline-block;
  text-decoration: none;
  cursor: zoom-in;       /* nice affordance */
`;


const ImageCard = styled.div`
  position: relative;
  width: 320px;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
`;

const ImagePreview = styled.img`
  display: block;
  width: 100%;
  height: 180px;
  object-fit: contain; 
`;

const ImageOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.58);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const OverlayName = styled.span`
  color: #fff;
  font-size: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* File attachment card (non-previewable types like zip) */
const FileCard = styled.a`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 12px 14px;
  border-radius: 12px;

  background: #f1f5f9;
  border: 1px solid #e5e7eb;

  min-height: 48px;

  text-decoration: none;
  cursor: pointer;

  transition: border-color 120ms ease, background 120ms ease;

  &:hover {
    background: #eef2ff;
    border-color: rgba(88, 101, 242, 0.45);
  }
`;

const FileIcon = styled.span`
  font-family: "Material Icons";
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #5865f2;
  flex: 0 0 auto;
`;

const FileName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const FileDownload = styled.span`
  font-family: "Material Icons";
  color: #94a3b8;
  opacity: 0.9;
  flex: 0 0 auto;
`;

const FileItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 12px 14px;
  border-radius: 12px;

  background: #f1f5f9;
  border: 1px solid #e5e7eb;

  min-height: 48px;
  text-decoration: none;
  cursor: pointer;

  transition: border-color 120ms ease, background 120ms ease;

  &:hover {
    background: #eef2ff;
    border-color: rgba(88, 101, 242, 0.45);
  }
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  text-decoration: none;
  color: inherit;

  &:hover ${FileName} {
    color: #534af5;
  }
`;

const FileItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const ExpandButton = styled.button.attrs({ type: 'button' })`
  background: #f3f4f6;
  border: none;
  border-radius: 0.4rem;
  align-items: center;
  display: flex;
  padding: 0.45rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  color: #534af5;
  transition: background 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`;

const PreviewWindow = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
  background: #fafafa;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 0.25rem;
  transition: opacity 0.3s ease;
  opacity: 1;
`;

const PreviewPdf = styled.iframe`
  width: 100%;
  height: 280px;
  border: none;
  border-radius: 0.25rem;
  transition: opacity 0.3s ease;
  opacity: 1;
`;

const PreviewText = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 0.85rem;
  color: #1f2937;
  background: #ffffff;
  padding: 0.5rem;
  border-radius: 0.25rem;
  max-height: 250px;
  overflow-y: auto;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;


const ChevronIcon = styled(MdKeyboardArrowDown)`
  font-size: 22px;
  color: #5865f2;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const PreviewLoading = styled.div`
  font-size: 14px;
  color: #64748b;
  padding: 12px;
  text-align: center;
`;

export default DiscordDraftSection;
